import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Flex
} from "@chakra-ui/react";
import { FaEdit } from "react-icons/fa";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import Loading from "components/Loading/Loading";
import SearchTable2 from "components/Tables/SearchTable2";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { drvService } from "services/drvService";
import { DrvFilter } from "./DrvFilter";
import { DRV_LIST_URL_DEFAULTS } from "utils/listUrlSync";
import { useListUrlSync } from "hooks/useListUrlSync";
import { ListFiltersActiveAlert } from "components/ListFiltersActiveAlert/ListFiltersActiveAlert";

const DrvList = () => {
    const { t } = useTranslation();
    const { searchParams, setSearchParams, hasActiveFilters } = useListUrlSync(DRV_LIST_URL_DEFAULTS);
    const [response, setResponse] = useState(undefined);
    const showToast = useCustomToast();
    const columns = useMemo(() => [
        {
            Header: t("digital-collections.drv.list.table.header.number"),
            accessor: "numero",
        },
        {
            Header: t("digital-collections.drv.list.table.header.type"),
            accessor: "tipo",
        },
        {
            Header: t("digital-collections.drv.list.table.header.plate"),
            accessor: "placa",
        },
        {
            Header: t("digital-collections.drv.list.table.header.agent-enrollment"),
            accessor: "matricula_agente",
        },
        {
            Header: t("digital-collections.drv.list.table.header.agent-name"),
            accessor: "nome_agente",
        },
        {
            Header: t("digital-collections.drv.list.table.header.reason"),
            accessor: "motivo",
        },
        {
            Header: t("digital-collections.drv.list.table.header.date"),
            accessor: "data",
            id: "data_criacao"
        },
        {
            Header: t('digital-collections.drv.list.table.header.operations'),
            disableSortBy: true,
            actions: [
                {
                    label: t('system.actions.edit'),
                    onClick: (row) => { console.log(row) },
                    icon: <FaEdit />
                }
            ]
        }
    ], []);

    const fetchData = async (params = {}) => {
        params.orderBy = params.orderBy ?? searchParams.orderBy ?? "data_criacao";
        params.orderDirection = params.orderDirection ?? searchParams.orderDirection ?? "desc";
        if (response) setResponse(undefined);

        try {
            params = { ...searchParams, ...params };
            setSearchParams(params);
            const data = await drvService.list(params);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            //showToast({ title: t(err), status: "error" });
        }
    };

    const clearDrvFilters = () => {
        fetchData({
            page: 1,
            filters: {
                placa: null,
                status: null,
                tipo: null,
                data_inicial: null,
                data_final: null,
            },
        });
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <Flex gap="5" direction="column" pt={{ base: "150px", sm: "125px", lg: "90px" }}>
            <Flex w="100%" justify="flex-end" align="center" direction={{ sm: "column", md: "row" }}>
                <DrvFilter
                    filterSnapshot={searchParams.filters}
                    onFilter={(filters) => fetchData({ filters })}
                />
            </Flex>
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <CardBody>
                    <ListFiltersActiveAlert show={hasActiveFilters} onClear={clearDrvFilters} />
                    {response ? (
                        <Box position="relative">
                            <SearchTable2
                                columnsData={columns}
                                tableData={response.content}
                                pageableData={toTablePageableData(response, { defaultField: "data_criacao", ...searchParams })}
                                onChange={fetchData}
                                disableSearch={true}
                            />

                        </Box>
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
            </Card>
        </Flex>
    );
};

export default DrvList;
